import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import AppContext from '../context/AppContext';
import UserTagsAPI, { TagVocabItem, UserPatternRow } from '../api/UserTagsAPI';

// "Your patterns" — the retrieval payoff for user-selected tags. Aggregates the player's own tags
// across every game they've tagged: one row per theme, how many games carry it, and links into those
// games. This is the cross-game view single-game analysis can't give — the reason the tags exist.
export default function MyPatternsContainer(): React.ReactNode {
  const { isAuthenticated, openLogin } = useContext(AppContext);
  const [rows, setRows] = useState<UserPatternRow[] | null>(null);
  const [vocab, setVocab] = useState<Record<string, TagVocabItem>>({});
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;
    async function load() {
      try {
        const [patterns, vocabulary] = await Promise.all([
          UserTagsAPI.getMyPatterns(),
          UserTagsAPI.getVocabulary(),
        ]);
        if (cancelled) {
          return;
        }
        const map: Record<string, TagVocabItem> = {};
        for (const v of vocabulary) {
          map[v.Slug] = v;
        }
        setVocab(map);
        setRows(patterns);
      } catch {
        if (!cancelled) {
          setError(true);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="sc-mypatterns sc-mypatterns-empty">
        <h1>Your patterns</h1>
        <p>
          Tag your moves and games while you review, and the habits you keep repeating collect
          here, across every game, not one at a time.
        </p>
        <button type="button" className="btn btn-primary" onClick={openLogin}>
          Log in to see your patterns
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sc-mypatterns">
        <h1>Your patterns</h1>
        <p className="text-danger">Couldn't load your patterns. Try again in a moment.</p>
      </div>
    );
  }

  if (rows == null) {
    return (
      <div className="sc-mypatterns sc-mypatterns-loading">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="sc-mypatterns">
      <h1>Your patterns</h1>
      <p className="sc-mypatterns-lead">
        The reads you&apos;ve tagged, gathered across every game. The ones near the top are the
        habits showing up most often.
      </p>

      {rows.length === 0 ? (
        <div className="sc-mypatterns-none">
          <p>
            You haven&apos;t tagged anything yet. Open a game, and under any move you&apos;ll find{' '}
            <em>Your private read on this move</em>. Or tag the whole game with{' '}
            <em>How this game went</em>.
          </p>
        </div>
      ) : (
        <ul className="sc-mypatterns-list">
          {rows.map((r) => {
            const def = vocab[r.Theme];
            return (
              <li key={r.Theme} className="sc-mypattern">
                <div className="sc-mypattern-head">
                  <span className="sc-mypattern-label">{def?.Label ?? r.Theme}</span>
                  <span className={`sc-mypattern-scope sc-scope-${r.Scope.toLowerCase()}`}>
                    {r.Scope === 'Game' ? 'whole game' : 'a move'}
                  </span>
                  <span className="sc-mypattern-count">
                    {r.GameCount} {r.GameCount === 1 ? 'game' : 'games'}
                  </span>
                </div>
                {def?.Hint ? <div className="sc-mypattern-hint">{def.Hint}</div> : null}
                <div className="sc-mypattern-samples">
                  {r.SampleGameIds.map((id) => (
                    <a key={id} className="sc-mypattern-game" href={`#/?gameId=${id}`}>
                      game #{id}
                    </a>
                  ))}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
